import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { type AdminPlugin, pluginsApi } from "@/api/plugins-api"

import { PluginDefinitionForm } from "./plugin-definition-form"
import { usePluginQueryKey } from "./use-plugin-query-key"

export function PluginDefinitionEditor({
  plugin,
  onSaved,
}: {
  plugin: AdminPlugin
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const versions = useQuery({
    queryKey: pluginKey("versions", plugin.id),
    queryFn: () => pluginsApi.versions(plugin.id),
  })
  const latest = versions.data?.versions.find((version) => version.id === plugin.published_version)
  const templates = useQuery({
    queryKey: pluginKey("templates"),
    queryFn: pluginsApi.templates,
    enabled: Boolean(plugin.template_id && !latest),
  })
  const defaults = !latest
    ? templates.data?.templates.find(
        (template) =>
          template.id === plugin.template_id && template.version === plugin.template_version
      )?.defaults
    : undefined
  const archived = useQuery({
    queryKey: pluginKey("source", plugin.id, latest?.id),
    queryFn: () => pluginsApi.source(plugin.id, latest!.id),
    enabled: Boolean(latest?.source_blob),
  })
  if (
    versions.isPending ||
    (latest?.source_blob && archived.isPending) ||
    (plugin.template_id && !latest && templates.isPending)
  )
    return <p role="status">{t("plugins.loading")}</p>
  if (versions.error || archived.error) return <p role="alert">{t("plugins.error")}</p>
  if (plugin.template_id && !latest && (templates.error || !defaults))
    return <p role="alert">{t("plugins.error")}</p>
  return (
    <PluginDefinitionForm
      defaults={defaults}
      latest={latest}
      original={archived.data?.source ?? ""}
      plugin={plugin}
      onSaved={onSaved}
    />
  )
}
